/**
 * Environment Configuration
 *
 * Zod-validated import.meta.env with defaults. Fails fast at module load
 * with a readable message when the environment is misconfigured.
 * VITE_API_BASE_URL is the single canonical WordPress REST API base URL.
 */

import { z } from 'zod';

/** 'true'/'false' string flag -> boolean, with default */
const boolFlag = (def: boolean) =>
  z.enum(['true', 'false']).default(def ? 'true' : 'false').transform((v) => v === 'true');

export const envSchema = z.looseObject({
  // WordPress / CMS — CANONICAL API base URL (decision D4)
  VITE_API_BASE_URL: z.string().url().default('https://api.kgprodesign.com/kgpro/wp-json'),
  VITE_CLIENT_THEME: z.string().default('default'),
  VITE_BLOG_NAME: z.string().default('Blog'),
  VITE_SITE_NAME: z.string().default('KG ProDesign'),
  VITE_MENU_CONFIG: z.string().default(''),
  VITE_HERO_HEADLINE: z.string().optional(),
  // Feature flags: dark mode is the only BUILD-level flag (ThemeProvider);
  // the others are runtime settings (features.* via /api/config/public).
  VITE_ENABLE_DARK_MODE: boolFlag(true),
});

export type Env = z.infer<typeof envSchema>;

const parsed = envSchema.safeParse(import.meta.env);
if (!parsed.success) {
  // Fail fast with a readable message
  throw new Error(`[config/env] Invalid environment configuration:\n${z.prettifyError(parsed.error)}`);
}
export const env: Env = parsed.data;
